import { useCallback, useEffect, useRef } from 'react'
import { useAnimationStore } from '../store/animationStore.js'
import type { AnimationType } from '../store/animationStore.js'
import type { Coord } from '@fleetduel/shared'
import { coordKey } from '@fleetduel/shared'

/**
 * Hook that bridges the animation store with component rendering.
 * Provides timing control, skip-ahead support, and per-cell animation state queries.
 */
export function useAnimation() {
  const current = useAnimationStore(s => s.current)
  const phase = useAnimationStore(s => s.phase)
  const skipRequested = useAnimationStore(s => s.skipRequested)
  const reduceMotion = useAnimationStore(s => s.reduceMotion)
  const inputLocked = useAnimationStore(s => s.inputLocked)
  const advancePhase = useAnimationStore(s => s.advancePhase)
  const skip = useAnimationStore(s => s.skip)
  const resolve = useAnimationStore(s => s.resolve)

  const isAnimating = current !== null
  const timerRefs = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())

  // When skip is requested, clear all pending timers and resolve
  useEffect(() => {
    if (skipRequested && current) {
      for (const id of timerRefs.current) clearTimeout(id)
      timerRefs.current.clear()
      resolve()
    }
  }, [skipRequested, current, resolve])

  /**
   * Schedule a callback after `ms` milliseconds.
   * Respects reduceMotion (fires at 150ms) and skip-ahead (fires immediately).
   */
  const phaseTimer = useCallback((ms: number, onComplete: () => void) => {
    const store = useAnimationStore.getState()
    if (store.reduceMotion) {
      const id = setTimeout(onComplete, 150)
      timerRefs.current.add(id)
      return () => { clearTimeout(id); timerRefs.current.delete(id) }
    }
    if (store.skipRequested) {
      onComplete()
      return () => {}
    }
    const id = setTimeout(() => {
      timerRefs.current.delete(id)
      if (!useAnimationStore.getState().skipRequested) {
        onComplete()
      }
    }, ms)
    timerRefs.current.add(id)
    return () => { clearTimeout(id); timerRefs.current.delete(id) }
  }, [])

  /**
   * Check if a specific cell is the target of the current animation.
   */
  const isCellAnimating = useCallback((coord: Coord): boolean => {
    if (!current) return false
    if (coordKey(current.target) === coordKey(coord)) return true
    if (current.cells) {
      return current.cells.some(c => coordKey(c.coord) === coordKey(coord))
    }
    return false
  }, [current])

  /**
   * Get the current animation type (if any) for use in FX rendering.
   */
  const currentType: AnimationType | null = current?.type ?? null

  return {
    current,
    currentType,
    phase,
    skipRequested,
    reduceMotion,
    inputLocked,
    isAnimating,
    phaseTimer,
    isCellAnimating,
    advancePhase,
    skip,
    resolve,
  }
}
